/**
 * Defect points (AUT-465): which KIND of part a point sits on, and which
 * types a point of that kind offers. Owner decision 2026-09-29 #3: glass and
 * lights stay pointable on the body steps, so the type sheet depends on the
 * part under the finger — body panels get the nine body types, glazing the
 * glass ones, lamps the light ones, «Другое» everywhere.
 *
 * The kind is DERIVED from `DEFECT_ZONE_GROUPS` (no zone list is copied
 * here), with one named override: a mirror is grouped under `glass` for
 * display (cracked mirror glass), but a point on a mirror is a point on its
 * painted housing — a dent or a scratch — so it takes the body types. A guard
 * test cross-checks the result against the geometry's paint (`glass` / `lamp`)
 * on every drawn part of every view.
 */
import { DEFECT_ZONE_GROUPS } from '../defect-zone.js';
export const POINT_ZONE_KINDS = ['body', 'glass', 'lights'];
/**
 * The shape of a point id: an RFC 4122 uuid (version 1–8, variant 10xx),
 * case-insensitive. ONE pattern for both gates — the shared validator and the
 * api's strict Zod (`z.string().regex(DEFECT_POINT_ID_PATTERN)`) — so a client
 * id the app accepts is never a server 400. A client mints it with
 * `crypto.randomUUID()` (v4).
 */
export const DEFECT_POINT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const isDefectPointId = (value) => DEFECT_POINT_ID_PATTERN.test(value);
/** Zones whose display group says `glass` but whose point is a body point. */
const isMirror = (zone) => zone.startsWith('mirror_');
const GLASS = new Set(DEFECT_ZONE_GROUPS.glass);
const LIGHTS = new Set(DEFECT_ZONE_GROUPS.lights);
/** The kind of part a point on `zone` sits on. */
export const pointZoneKindOf = (zone) => {
    if (isMirror(zone))
        return 'body';
    if (GLASS.has(zone))
        return 'glass';
    if (LIGHTS.has(zone))
        return 'lights';
    return 'body';
};
/**
 * The point types of catalogue v2 (owner decisions 2026-09-29 #1–#3). Body:
 * 8 findings + «Другое» = `CHECKLIST_LIMITS.maxTypesPerPoint`. Order is the
 * sheet order.
 */
export const POINT_TYPES_V2 = {
    body: [
        'dent',
        'rust',
        'paint_chip',
        'scratch',
        'paint_mismatch',
        'filler',
        'panel_gap',
        'body_repair_marks',
        'other',
    ],
    glass: ['glass_crack', 'glass_chip', 'glass_replaced', 'other'],
    lights: ['light_fault', 'lens_fogged', 'lens_damage', 'other'],
};
/**
 * Every type a point may carry on a step with these zones: the union of the
 * kinds the zones cover, in kind order, «Другое» last. It is the step's chip
 * allow-list in point mode.
 */
export const pointTypesOnZones = (zones, types) => {
    const kinds = new Set(zones.map(pointZoneKindOf));
    const union = POINT_ZONE_KINDS.filter((kind) => kinds.has(kind)).flatMap((kind) => types[kind]);
    const unique = [...new Set(union)];
    return unique.includes('other') ? [...unique.filter((c) => c !== 'other'), 'other'] : unique;
};
//# sourceMappingURL=pointKinds.js.map